import { FormConditionalService } from './FormConditionalService';
import { FormParameterValues } from '@client/hooks/useFormParameters';

export interface Position {
  sectionIndex: number;
  subsectionIndex: number;
  subsectionId: string;
}

export interface NavigationProgress {
  completedSubsections: Set<string>;
  visitedSubsections: Set<string>;
  isFormComplete: boolean;
  currentPosition: Position;
  maxAccessiblePosition: Position;
}

export interface NavigationValidation {
  canNavigate: boolean;
  reason?: string;
}

export class NavigationService {
  private conditionalService: FormConditionalService;
  private wizardUnrestrictedNavEnabled: boolean;

  constructor(
    private formDefinition: PolicyFormDefinition,
    questionRegistry: Record<string, PolicyFormQuestion>,
    wizardUnrestrictedNavEnabled: boolean = false,
  ) {
    // Initialize with empty data - will be updated via updateContext
    this.conditionalService = new FormConditionalService(formDefinition, {}, {});
    this.wizardUnrestrictedNavEnabled = wizardUnrestrictedNavEnabled;
  }

  /**
   * Updates the conditional service context with current form data and parameters
   */
  updateContext(formData: PolicyFormSubmissionData, parameters: FormParameterValues = {}) {
    this.conditionalService.update(formData, parameters);
  }

  createPosition(sectionIndex: number, subsectionIndex: number): Position {
    const section = this.formDefinition.sections[sectionIndex];
    const subsection = section?.subsections[subsectionIndex];

    if (!section || !subsection) {
      throw new Error(`Invalid position: section ${sectionIndex}, subsection ${subsectionIndex}`);
    }

    return {
      sectionIndex,
      subsectionIndex,
      subsectionId: subsection.id,
    };
  }

  getFirstPosition(): Position {
    return this.createPosition(0, 0);
  }

  getLastPosition(): Position {
    const lastSectionIndex = this.formDefinition.sections.length - 1;
    const lastSection = this.formDefinition.sections[lastSectionIndex];
    const lastSubsectionIndex = lastSection.subsections.length - 1;

    return this.createPosition(lastSectionIndex, lastSubsectionIndex);
  }

  getNextPosition(current: Position): Position | null {
    const currentSection = this.formDefinition.sections[current.sectionIndex];

    if (!currentSection) return this.getLastPosition();

    if (current.subsectionIndex < currentSection.subsections.length - 1) {
      return this.createPosition(current.sectionIndex, current.subsectionIndex + 1);
    }

    if (current.sectionIndex < this.formDefinition.sections.length - 1) {
      return this.createPosition(current.sectionIndex + 1, 0);
    }

    return null;
  }

  getNextVisiblePosition(
    current: Position,
    formData: PolicyFormSubmissionData,
    parameters: FormParameterValues = {},
  ): Position | null {
    // Update conditional service with current form data and parameters
    this.conditionalService.update(formData, parameters);

    let next = this.getNextPosition(current);
    const maxIterations = this.formDefinition.sections.reduce(
      (sum, section) => sum + section.subsections.length,
      0,
    );
    let iterations = 0;

    while (next && iterations < maxIterations) {
      if (this.conditionalService.isPositionVisible(next.sectionIndex, next.subsectionIndex)) {
        return next;
      }
      next = this.getNextPosition(next);
      iterations++;
    }

    return null;
  }

  getPreviousPosition(current: Position): Position | null {
    if (current.subsectionIndex > 0) {
      return this.createPosition(current.sectionIndex, current.subsectionIndex - 1);
    }

    if (current.sectionIndex > 0) {
      const previousSection = this.formDefinition.sections[current.sectionIndex - 1];
      return this.createPosition(current.sectionIndex - 1, previousSection.subsections.length - 1);
    }

    return null;
  }

  getPreviousVisiblePosition(
    current: Position,
    formData: PolicyFormSubmissionData,
    parameters: FormParameterValues = {},
  ): Position | null {
    // Update conditional service with current form data and parameters
    this.conditionalService.update(formData, parameters);

    let prev = this.getPreviousPosition(current);
    const maxIterations = this.formDefinition.sections.reduce(
      (sum, section) => sum + section.subsections.length,
      0,
    );
    let iterations = 0;

    while (prev && iterations < maxIterations) {
      if (this.conditionalService.isPositionVisible(prev.sectionIndex, prev.subsectionIndex)) {
        return prev;
      }
      prev = this.getPreviousPosition(prev);
      iterations++;
    }

    return null;
  }

  isSubsectionComplete(
    subsectionId: string,
    formData: PolicyFormSubmissionData,
    visitedSubsections: Set<string>,
    currentSubsectionId?: string,
  ): boolean {
    const subsection = this.findSubsectionById(subsectionId);
    if (!subsection) return false;

    const isCurrentSubsection = currentSubsectionId === subsectionId;
    const isLastVisibleSubsection = this.isLastVisibleSubsectionId(subsectionId);

    if (isCurrentSubsection && !isLastVisibleSubsection) {
      return false;
    }

    if (!visitedSubsections.has(subsectionId)) {
      return false;
    }

    // Use conditional service to check if subsection is complete
    return this.conditionalService.isSubsectionComplete(subsection);
  }

  canNavigateToPosition(
    target: Position,
    formData: PolicyFormSubmissionData,
    visitedSubsections: Set<string>,
    currentSubsectionId?: string,
  ): NavigationValidation {
    // Wizard unrestricted navigation bypass: allow navigation to any visible subsection
    if (this.wizardUnrestrictedNavEnabled) {
      return { canNavigate: true };
    }

    const subsection = this.findSubsectionById(target.subsectionId);
    if (!subsection) {
      return { canNavigate: false, reason: 'Subsection not found' };
    }

    const progress = this.calculateProgress(formData, visitedSubsections, currentSubsectionId, {});

    // Allow navigation to visited subsections
    if (visitedSubsections.has(target.subsectionId)) {
      return { canNavigate: true };
    }

    // Allow navigation to completed subsections
    if (progress.completedSubsections.has(target.subsectionId)) {
      return { canNavigate: true };
    }

    // Check if the position is accessible (within the max accessible range)
    const isAccessible = this.isPositionAccessible(target, progress.maxAccessiblePosition);
    if (!isAccessible) {
      return { canNavigate: false, reason: 'Complete previous sections first' };
    }

    return { canNavigate: true };
  }

  canNavigateFromPosition(
    current: Position,
    formData: PolicyFormSubmissionData,
    parameters: FormParameterValues = {},
  ): NavigationValidation {
    // Update conditional service with current form data and parameters
    this.conditionalService.update(formData, parameters);

    const subsection = this.findSubsectionById(current.subsectionId);
    if (!subsection) {
      return { canNavigate: false, reason: 'Current subsection not found' };
    }

    // Use conditional service to get missing fields
    const missingFields = this.conditionalService.getMissingRequiredFields(subsection);

    if (missingFields.length > 0) {
      const missingRefs = missingFields.map(field => field.questionRef);
      return {
        canNavigate: false,
        reason: `Complete required fields: ${missingRefs.join(', ')}`,
      };
    }

    return { canNavigate: true };
  }

  calculateProgress(
    formData: PolicyFormSubmissionData,
    visitedSubsections: Set<string>,
    currentSubsectionId?: string,
    parameters: FormParameterValues = {},
  ): NavigationProgress {
    // Update conditional service with current form data and parameters
    this.conditionalService.update(formData, parameters);
    const completedSubsections = new Set<string>();
    let maxAccessiblePosition = this.getFirstPosition();
    let foundFirstIncomplete = false;

    const visibleSections = this.conditionalService.getVisibleSections();
    let totalVisibleSubsections = 0;

    for (const { section, subsections } of visibleSections) {
      const sectionIndex = this.formDefinition.sections.indexOf(section);

      for (const subsection of subsections) {
        const subsectionIndex = section.subsections.indexOf(subsection);
        totalVisibleSubsections++;

        const isComplete = this.isSubsectionComplete(
          subsection.id,
          formData,
          visitedSubsections,
          currentSubsectionId,
        );

        if (isComplete) {
          completedSubsections.add(subsection.id);
        } else if (!foundFirstIncomplete) {
          maxAccessiblePosition = this.createPosition(sectionIndex, subsectionIndex);
          foundFirstIncomplete = true;
        }
      }
    }

    if (!foundFirstIncomplete) {
      maxAccessiblePosition = this.getLastPosition();
    }

    const isFormComplete = completedSubsections.size === totalVisibleSubsections;

    return {
      completedSubsections,
      visitedSubsections: new Set(visitedSubsections),
      isFormComplete,
      currentPosition: maxAccessiblePosition,
      maxAccessiblePosition,
    };
  }

  private findSubsectionById(subsectionId: string): PolicyFormSubSection | null {
    for (const section of this.formDefinition.sections) {
      const subsection = section.subsections.find(sub => sub.id === subsectionId);
      if (subsection) return subsection;
    }
    return null;
  }

  private isLastSubsectionId(subsectionId: string): boolean {
    const lastPosition = this.getLastPosition();
    return lastPosition.subsectionId === subsectionId;
  }

  private isLastVisibleSubsectionId(subsectionId: string): boolean {
    const visibleSections = this.conditionalService.getVisibleSections();

    let lastVisibleSubsectionId: string | null = null;
    for (const { subsections } of visibleSections) {
      if (subsections.length > 0) {
        lastVisibleSubsectionId = subsections[subsections.length - 1].id;
      }
    }

    return lastVisibleSubsectionId === subsectionId;
  }

  private isPositionAccessible(target: Position, maxAccessible: Position): boolean {
    if (target.sectionIndex < maxAccessible.sectionIndex) return true;
    if (target.sectionIndex > maxAccessible.sectionIndex) return false;
    return target.subsectionIndex <= maxAccessible.subsectionIndex;
  }

  getMissingRequiredFieldsForSubsection(subsection: PolicyFormSubSection): Array<{
    questionRef: string;
    question: PolicyFormQuestion;
  }> {
    return this.conditionalService.getMissingRequiredFields(subsection);
  }
}
